import { Platform } from "react-native";
import { cva, VariantProps } from "class-variance-authority";

import { cn } from "@/utils/tailwind";

const DISABLED_FILLED =
  "disabled:bg-foreground/[0.06] disabled:active:bg-foreground/[0.06] disabled:border-transparent disabled:shadow-none";
const DISABLED_TRANSPARENT = "disabled:bg-transparent disabled:shadow-none";
export const DISABLED_TEXT = "group-disabled:text-foreground/[0.38]";

export const buttonVariants = cva(
  cn(
    "group relative shrink-0 flex-row items-center justify-center gap-2 rounded-full shadow-none outline",
    Platform.select({
      web: "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive focus-visible:ring-ring/50 focus-visible:border-ring whitespace-nowrap outline-none transition-all focus-visible:ring-[3px] disabled:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
    }),
  ),
  {
    variants: {
      variant: {
        default: cn(
          "active:bg-primary/90 bg-primary border border-black/10 shadow-sm shadow-black/5",
          DISABLED_FILLED,
          Platform.select({ web: "hover:bg-primary/90" }),
        ),
        destructive: cn(
          "dark:active:bg-input/50 dark:bg-input/30 active:bg-accent border-destructive border bg-transparent shadow-sm shadow-black/5",
          DISABLED_FILLED,
          Platform.select({
            web: "dark:hover:bg-input/50 hover:bg-accent",
          }),
        ),
        outline: cn(
          "dark:active:bg-input/50 dark:bg-input/30 active:bg-accent border-sidebar bg-secondary dark:border-input border border-[1.5px] shadow-sm shadow-black/5",
          DISABLED_FILLED,
          Platform.select({
            web: "dark:hover:bg-input/50 hover:bg-accent",
          }),
        ),
        secondary: cn(
          "active:bg-secondary/80 bg-secondary border border-black/10 shadow-sm shadow-black/5",
          DISABLED_FILLED,
          Platform.select({ web: "hover:bg-secondary/80" }),
        ),
        ghost: cn(
          "dark:active:bg-accent/50 active:bg-accent border-transparent",
          DISABLED_TRANSPARENT,
          Platform.select({
            web: "dark:hover:bg-accent/50 hover:bg-accent",
          }),
        ),
        link: cn("border-transparent", DISABLED_TRANSPARENT),
        chipSelected: cn(
          "bg-chart-one border-chart-one rounded-md shadow-sm shadow-black/5",
          DISABLED_FILLED,
          Platform.select({ web: "hover:bg-chart-one/90" }),
        ),
        chipUnselected: cn(
          "bg-chart-four/40 border-chart-six rounded-md shadow-sm shadow-black/5",
          DISABLED_FILLED,
          Platform.select({ web: "hover:bg-chart-four/80" }),
        ),
      },
      size: {
        default: cn(
          "h-10 px-4 py-2 sm:h-9",
          Platform.select({ web: "has-[>svg]:px-3" }),
        ),
        sm: cn(
          "h-9 gap-1.5 rounded-md px-3 sm:h-8",
          Platform.select({ web: "has-[>svg]:px-2.5" }),
        ),
        lg: cn(
          "h-11 rounded-md px-6 sm:h-10",
          Platform.select({ web: "has-[>svg]:px-4" }),
        ),
        xl: cn(
          "h-20 rounded-md px-4 sm:h-16",
          Platform.select({ web: "has-[>svg]:px-6" }),
        ),
        icon: "h-10 w-10 border-none sm:h-9 sm:w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export const buttonTextVariants = cva(
  cn(
    "font-primary-medium text-foreground text-sm",
    Platform.select({ web: "pointer-events-none transition-colors" }),
  ),
  {
    variants: {
      variant: {
        default: `text-primary-foreground ${DISABLED_TEXT}`,
        destructive: `text-destructive ${DISABLED_TEXT}`,
        outline: `group-active:text-accent-foreground web:group-hover:text-accent-foreground ${DISABLED_TEXT}`,
        secondary: `text-secondary-foreground ${DISABLED_TEXT}`,
        ghost: `group-active:text-accent-foreground web:group-hover:text-accent-foreground ${DISABLED_TEXT}`,
        link: `native:border-b native:border-primary web:underline ${DISABLED_TEXT} underline-offset-4`,
        chipSelected: `text-foreground ${DISABLED_TEXT}`,
        chipUnselected: `text-foreground ${DISABLED_TEXT}`,
      },
      size: {
        default: "",
        sm: "",
        lg: "",
        xl: "",
        icon: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonVariantType = VariantProps<typeof buttonVariants>["variant"];
